import { styled } from '@linaria/react';
import { Text } from '@mantine/core';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting';
import { resolvePhaseLabels } from '../../../helpers/phase-labels/phase-labels';
import type { PhaseLabel } from '../../../helpers/phase-labels/phase-labels.type';
import { newCOLORS } from '../../../styles/colors';
import { skyLight } from '../../../styles/design-tokens';
import {
  PHASES,
  getPhasesMetadata,
} from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.constants';
import { SprintData } from '../hooks/dashboards.hooks.type';
import { buildCumulativeFlowChartData } from './cumulative-flow-chart.helpers';

interface CumulativeFlowChartProps {
  data: SprintData | undefined;
  selectedMetric: 'tasks' | 'points';
  /**
   * Optional `phases` companion array sourced from the same API response
   * that produced `data`. Drives per-workflow display labels for the
   * Recharts tooltip and legend; absent values fall back to the
   * `common.workflow_steps.*` i18n defaults.
   */
  phases?: PhaseLabel[];
}

export const CumulativeFlowChart = ({ data, selectedMetric, phases }: CumulativeFlowChartProps) => {
  const { t } = useTranslation();
  const resolvedLabels = useMemo(() => resolvePhaseLabels(phases, t), [phases, t]);
  const phasesMetadata = useMemo(() => getPhasesMetadata(t, resolvedLabels), [t, resolvedLabels]);
  // Recharts stacks the first Area added at the bottom. Iterate PHASES in
  // reverse so `Done` (Completed) renders as the bottom band of the stack.
  const stackOrder = useMemo(() => [...PHASES].reverse(), []);

  const chartData = useMemo(() => buildCumulativeFlowChartData(data, selectedMetric), [data, selectedMetric]);

  if (!data || chartData.length === 0) {
    return (
      <EmptyChartMessage>
        <Text>No flow data available for the selected sprint.</Text>
      </EmptyChartMessage>
    );
  }

  return (
    <ChartWrapper>
      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 0 }}>
            <defs>
              {stackOrder.map((phaseId) => {
                const phaseMetadata = phasesMetadata.find((p) => p.id === phaseId);
                if (!phaseMetadata) return null;
                return (
                  <linearGradient
                    key={`gradient-${phaseMetadata.id}`}
                    id={`color-${phaseMetadata.id}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="20%" stopColor={phaseMetadata.color} stopOpacity={0.6} />
                    <stop offset="80%" stopColor={phaseMetadata.color} stopOpacity={0.3} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => dayjs(value).format('MMM D')}
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
              padding={{ right: 10 }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
              label={{
                value: selectedMetric === 'tasks' ? 'Tasks' : 'Points',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: 11 },
                offset: 10,
              }}
            />
            <Tooltip
              formatter={(value: any, name: any) => [value, phasesMetadata.find((p) => p.id === name)?.label || name]}
              labelFormatter={(label: any) => `Date: ${formatDate(label, { format: 'MMM D, YYYY' })}`}
            />
            <Legend />
            {stackOrder.map((phaseId) => {
              const phaseMetadata = phasesMetadata.find((p) => p.id === phaseId);
              if (!phaseMetadata) return null;
              return (
                <Area
                  key={phaseMetadata.id}
                  type="monotone"
                  dataKey={phaseMetadata.id as string}
                  stackId="1"
                  stroke={phaseMetadata.color}
                  fill={`url(#color-${phaseMetadata.id})`}
                  fillOpacity={0.6}
                  strokeOpacity={1}
                  strokeWidth={1}
                  name={phaseMetadata.label}
                  connectNulls={false}
                  animationDuration={0}
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </ChartContainer>
    </ChartWrapper>
  );
};

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  width: 100%;
`;

const EmptyChartMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 300px;
  background-color: ${newCOLORS.lightGray};
  border-radius: 4px;
  padding: 16px;
  text-align: center;
`;
